import React, { useState } from "react";
import { X, Loader2, Zap, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Add an electricity entry: either a "purchase" from the supplier (expense)
 * or a "recovery" from a tenant / unit meter (income).
 */
export default function ElectricityFormModal({ propertyId, units, tenants, defaultType, onClose, onSaved }) {
  const [txnType, setTxnType] = useState(defaultType || "recovery");
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    kwh: "",
    unit_id: "",
    tenant_id: "",
    counterparty: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const isPurchase = txnType === "purchase";

  // When selecting a unit for recovery, auto-fill tenant if that unit is occupied
  const pickUnit = (uid) => {
    const t = (tenants || []).find(
      (x) => x.unit_id === uid && !(x.status === "ended" || x.archived)
    );
    setForm((f) => ({ ...f, unit_id: uid, tenant_id: t ? t.id : "" }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    if (!isPurchase && !form.unit_id) { toast.error("Pick a unit for the recovery"); return; }
    setBusy(true);
    try {
      await api.post("/electricity", {
        property_id: propertyId,
        txn_type: txnType,
        date: form.date,
        amount: Number(form.amount),
        kwh: Number(form.kwh) || 0,
        unit_id: isPurchase ? null : (form.unit_id || null),
        tenant_id: isPurchase ? null : (form.tenant_id || null),
        counterparty: form.counterparty || "",
        notes: form.notes || "",
      });
      toast.success(isPurchase ? "Purchase logged" : "Recovery logged");
      onSaved?.();
    } catch {
      toast.error("Could not save electricity entry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="electricity-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-lg fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 grid place-items-center">
              <Zap className="w-4 h-4" />
            </div>
            <h3 className="font-display font-semibold text-lg">Log electricity {isPurchase ? "purchase" : "recovery"}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition" data-testid="electricity-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-slate-100 rounded-lg">
            <button
              type="button"
              onClick={() => setTxnType("recovery")}
              data-testid="electricity-type-recovery"
              className={`h-9 rounded-md text-sm font-medium transition ${!isPurchase ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Recovery (income)
            </button>
            <button
              type="button"
              onClick={() => setTxnType("purchase")}
              data-testid="electricity-type-purchase"
              className={`h-9 rounded-md text-sm font-medium transition ${isPurchase ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Purchase (expense)
            </button>
          </div>

          <form onSubmit={save} className="space-y-4" data-testid="electricity-form">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Date</span>
                <input
                  type="date" required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="electricity-date"
                  className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Amount (R)</span>
                <input
                  type="number" step="0.01" min="0" required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="electricity-amount"
                  className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="500.00"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">kWh (optional)</span>
              <input
                type="number" step="0.01" min="0"
                value={form.kwh}
                onChange={(e) => setForm({ ...form, kwh: e.target.value })}
                data-testid="electricity-kwh"
                className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="e.g. 250"
              />
            </label>

            {!isPurchase && (
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Unit</span>
                <select
                  required
                  value={form.unit_id}
                  onChange={(e) => pickUnit(e.target.value)}
                  data-testid="electricity-unit"
                  className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                >
                  <option value="">Select unit…</option>
                  {(units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </label>
            )}

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
                {isPurchase ? "Supplier" : "Meter / voucher / tenant reference"}
              </span>
              <input
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                data-testid="electricity-counterparty"
                className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder={isPurchase ? "e.g. Eskom / City of Cape Town" : "Voucher #, meter reading, EFT ref"}
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Notes</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 h-16 resize-none"
                placeholder="Optional context"
              />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                data-testid="electricity-cancel-btn"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="electricity-save-btn"
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Save entry
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
